import { Box, Button, HStack, Icon, Stack, Text, useToast, VStack } from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import { useQueryClient } from '@tanstack/react-query';
import { DetailPageTask, Task } from 'api/types';
import SystemPartnerCard from 'components/SystemPartnerCard';
import { Warning } from 'components/Warning';
import { UserPermissions } from 'configs/routes';
import { useAxios } from 'context/AxiosContextProvider';
import { useUserContext } from 'context/UserContextProvider';
import { downloadFile } from 'helpers/downloadHelper';
import useDeleteServiceDocumentMutation from 'hooks/mutations/useDeleteServiceDocumentMutation';
import useUploadFileMutation from 'hooks/mutations/useUploadFileMutation';
import useAccountV2Query from 'hooks/useAccountV2Query';
import useTokenQuery from 'hooks/useTokenQuery';
import _, { get, isNil } from 'lodash';
import TaskBannerCard from 'pages/tasks/TaskBannerCard';
import CredentialCard from 'pages/workshop/CredentialCard';
import { createElement, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { IoIosWarning } from 'react-icons/io';
import { useNavigate, useParams } from 'react-router-dom';

import ActivityLogCard from '../../components/ActivityLogCard';
import ConversationCard from '../../components/conversations/ConversationCard';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import AlertModal from '../../components/modals/AlertModal';
import { StatusCard } from '../../components/StatusCard';
import { TasksTableCard } from '../../components/TasksTableCard';
import localconfig from '../../config';
import { dateTimeTransformer } from '../../helpers/dateTransformer';
import { getComponentBasedOnType, getStatus } from '../../helpers/general';
import { manageErrorResponse } from '../../helpers/manageErrorResponse';
import useAssignMutation from '../../hooks/mutations/useAssignMutation';
import useAssignNextTaskMutation from '../../hooks/mutations/useAssignNextTaskMutation';
import useCheckTaskMutation from '../../hooks/mutations/useCheckTaskMutation';
import useCreateConversationMessageMutation from '../../hooks/mutations/useCreateConversationMessageMutation';
import useDeleteServiceRecordDocumentMutations from '../../hooks/mutations/useDeleteServiceRecordDocumentMutations';
import useUpdateServiceRecordRequestStatus from '../../hooks/mutations/useUpdateServiceRecordStatusMutation';
import useUploadServiceRecordDocumentMutation from '../../hooks/mutations/useUploadServiceRecordDocumentMutation';
import useDsbFormVersionSchemaQuery from '../../hooks/useDsbFormVersionSchemaQuery';
import useFileQuery from '../../hooks/useFileQuery';
import useServiceRecordQuery from '../../hooks/useServiceRecordQuery';
import { ContentCard } from '../../layout/ContentCard';
import { ContentLayout } from '../../layout/ContentLayout';
import PageMetatags from '../../layout/PageMetatags';
import FileDisplay from '../FileDisplay';
import FileForm from '../FileForm';
import RecentlyCheckedCard from '../tasks/RecentlyCheckedCard';
import WorkshopCard from '../workshop/WorkshopCard';
import WorkshopWarning from '../workshop/WorkshopWarning';
import DocumentsCard from './DocumentsCard';

const ServiceRecordDetailsPage = () => {
  const toast = useToast();
  const axios = useAxios();
  const navigate = useNavigate();
  const { user, opUsers, statusCodes, statusTransitions, isMember } = useUserContext();
  const token = useTokenQuery();
  const { id, type } = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const serviceRecordId = Number(id);

  const serviceRecordQuery = useServiceRecordQuery(serviceRecordId);
  const queryToInvalidate = () => queryClient.invalidateQueries({ queryKey: useServiceRecordQuery.getKey(serviceRecordId) });

  const accountQuery = useAccountV2Query(serviceRecordQuery.data?.account_id);

  const isSystemPartnerFull = serviceRecordQuery?.data?.systempartner?.integration === 'full';
  const apiUrl = `${localconfig.apiBaseUrl}/op/v1/service-record/${serviceRecordQuery.data?.id}`;

  // id of service document type "service_record", one that need to be deleted on successful upload of file
  const serviceDocumentId = serviceRecordQuery.data?.service_activity?.service?.documents?.filter(
    (document) => document?.type === 'service_record',
  )[0]?.id;

  const deleteServiceDocumentMutation = useDeleteServiceDocumentMutation(serviceDocumentId);
  const uploadFileMutation = useUploadFileMutation(apiUrl, useServiceRecordQuery.getKey(serviceRecordId));
  const updateServiceRecordRequestStatusMutation = useUpdateServiceRecordRequestStatus(serviceRecordId);
  const legacyServiceRecordCheck = serviceRecordQuery.data !== undefined && serviceRecordQuery.data?.wordpress_order_id !== null;
  // deep copy of the service record object
  const legacyRecord = _.cloneDeep(serviceRecordQuery.data);
  //@ts-ignore
  delete legacyRecord?.data;

  const workshop = serviceRecordQuery.data?.workshop;
  const tasks = (serviceRecordQuery.data as { tasks?: DetailPageTask[] } | undefined)?.tasks;

  const dsbFormVersionSchemaQuery = useDsbFormVersionSchemaQuery(serviceRecordQuery.data?.dsb_form_version_id as number);

  const assignMutation = useAssignMutation(serviceRecordQuery.data?.open_task?.id as number, useServiceRecordQuery.getKey(serviceRecordId));

  const resourceKey = 'service_record';

  const hasOpenTask = serviceRecordQuery.data?.open_task != undefined;

  const dateTaskCheckedByUser = serviceRecordQuery.data?.open_task?.last_checked_at;
  const checkTaskMutation = useCheckTaskMutation(serviceRecordQuery.data?.open_task?.id as number, useServiceRecordQuery.getKey(serviceRecordId));

  // @ts-ignore
  const serviceRecordCodes = statusCodes?.service_record || {};

  const [url, setUrl] = useState('');
  const [downloadDocument, setDownloadDocument] = useState(undefined);
  const [canEdit, setCanEdit] = useState(false);
  const [click, setClick] = useState(false);
  const [mazdaHasWlanConnected, setMazdaHasWlanConnected] = useState(false);

  const fileQuery = useFileQuery(url);
  useEffect(() => {
    downloadFile(downloadDocument, fileQuery.data);
    setClick(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileQuery.data, click]);

  const isHondaAndHasOfficialDsb = serviceRecordQuery.data?.vehicle?.manufacturer?.make?.name === 'Honda';
  const assignNextTaskMutation = useAssignNextTaskMutation(type ?? '');

  const createConversationMessageMutation = useCreateConversationMessageMutation(serviceRecordQuery.data?.conversation?.id as number);
  const deleteServiceRecordDocumentMutations = useDeleteServiceRecordDocumentMutations();
  const uploadServiceRecordDocumentMutation = useUploadServiceRecordDocumentMutation();
  const sendRequest = async ({
    // @ts-ignore
    url,
    method = 'put',
    dataForEndpoint = {},
  }) => {
    const axiosCall =
      method !== 'delete'
        ? // @ts-ignore
          axios[method](url, dataForEndpoint)
        : // @ts-ignore
          axios[method](url);
    return await axiosCall
      .catch((err: { response: { status: number } }) => {
        manageErrorResponse(toast, err.response);
        return Promise.reject(err);
      }) // @ts-ignore
      .then(({ data }) => {
        queryClient.invalidateQueries({ queryKey: useAccountV2Query.getKey(accountQuery.data?.id!) });
        queryClient.invalidateQueries({
          queryKey: useServiceRecordQuery.getKey(serviceRecordId),
        });
        return data;
      });
  };

  const allStatusesAndAllStatusReasons = // @ts-ignore
    ['bmw', 'mini', 'rolls-royce', 'bmw-m'].includes(serviceRecordQuery.data?.vehicle?.manufacturer?.slug as string) // @ts-ignore
      ? statusCodes?.service_record_reasons
      : { ...statusCodes?.service_record_reasons, 700: {} };

  const onHoldStatusReasons = statusCodes?.service_record_reasons['300'];
  if (!allStatusesAndAllStatusReasons || !onHoldStatusReasons || dsbFormVersionSchemaQuery.isLoading || serviceRecordQuery.isLoading) {
    return <LoadingSpinner />;
  }

  if (!isHondaAndHasOfficialDsb && !isNil(onHoldStatusReasons['350'])) {
    //@ts-ignore
    delete onHoldStatusReasons['350'];
  } else if (isHondaAndHasOfficialDsb && isNil(onHoldStatusReasons['350'])) {
    //@ts-ignore
    onHoldStatusReasons['350'] = 'on_hold_transfer_to_honda';
  }

  const srRequestedDeletion = serviceRecordQuery.data?.status === 800;
  const srOnHoldRequestedDeletion = serviceRecordQuery.data?.status_reason === 303;

  const showAutomationInProgress =
    serviceRecordQuery.data?.status === 100 &&
    serviceRecordQuery.data?.open_task &&
    serviceRecordQuery.data?.open_task.user_id == null &&
    serviceRecordQuery.data?.open_task.automate === true &&
    serviceRecordQuery.data?.open_task.queued === true;

  // const reasons = ['bmw', 'mini', 'rolls-royce', 'bmw-m'].includes(
  //   // @ts-ignore
  //   serviceRecordQuery.data?.vehicle?.manufacturer?.slug,
  // ) // @ts-ignore
  //   ? statusCodes?.service_record_reasons // @ts-ignore
  //   : { ...statusCodes?.service_record_reasons, 700: {} };

  const reasons = { ...statusCodes?.service_record_reasons, 700: {} };

  let detailsCard = <ContentCard isLoading={true} />;

  const canChangeVin = user?.permissions?.includes(UserPermissions.SERVICEENTRYCHANGEVIN);
  const openTaskAssignedToCurrentUser = serviceRecordQuery.data?.open_task?.user_id === user?.id;
  const isCompleted = serviceRecordQuery.data?.status === 700;

  if (serviceRecordQuery.data?.dsb_form_version?.dsb_form?.slug) {
    detailsCard = createElement(getComponentBasedOnType(serviceRecordQuery.data?.dsb_form_version?.dsb_form?.slug), {
      isLoading: serviceRecordQuery.isFetching,
      record: legacyServiceRecordCheck ? legacyRecord : serviceRecordQuery.data,
      sendRequest,
      token,
      serviceRecordCodes,
      reasons: [],
      schema: dsbFormVersionSchemaQuery.data,
      workshopLanguage: workshop?.language,
      canChangeVin,
      onMazdaWlanConnectedChange: setMazdaHasWlanConnected,
    });
  }

  const isSrInPorgressManufacturerNotRegistered =
    serviceRecordQuery.data?.status === 100 &&
    serviceRecordQuery.data?.open_task === null &&
    (accountQuery.data?.status === 0 || accountQuery.data?.status === 100 || accountQuery.data?.status === 150);

  if (isNaN(serviceRecordId) || serviceRecordQuery.isError) {
    navigate('/404', { replace: true });
    return null;
  }

  return (
    <>
      {hasOpenTask && (
        <TaskBannerCard
          showAutomationInProgress={showAutomationInProgress}
          queryToInvalidate={queryToInvalidate}
          isRegistrationOfPortalInProgress={isSrInPorgressManufacturerNotRegistered || serviceRecordQuery.data?.idsb}
          task={serviceRecordQuery.data?.open_task as Task}
          opUsers={opUsers}
          currentUserId={user?.id}
          assignee={serviceRecordQuery.data?.open_task?.assignee}
          isLoading={serviceRecordQuery.isFetching}
          assign={() =>
            assignMutation.mutate({
              updated_at: get(serviceRecordQuery.data, 'open_task.updated_at', serviceRecordQuery.data?.updated_at)!,
            })
          }
          reassign={(user_id) =>
            assignMutation.mutate({
              force: true,
              user_id,
              updated_at: get(serviceRecordQuery.data, 'open_task.updated_at', serviceRecordQuery.data?.updated_at)!,
            })
          }
        />
      )}

      <ContentLayout
        sidebar={
          <>
            {type ? (
              <Button
                variant="primary"
                disabled={serviceRecordQuery.isFetching || !isMember('tasks:assign-next')}
                data-test-id={`assign`}
                onClick={() =>
                  assignNextTaskMutation.mutate(
                    //@ts-ignore
                    {},
                    {
                      onSuccess: (task: Task) => {
                        if (task?.id) {
                          navigate(`/app/op/service-record/${task.subject_id}/${type}`);
                        } else {
                          navigate(`/app/op/break`);
                        }
                      },
                    },
                  )
                }
              >
                {t('common:next_activity')}
              </Button>
            ) : null}

            <WorkshopCard statusCodes={statusCodes?.workshop} isLoading={serviceRecordQuery.isFetching} workshop={workshop} />
            <CredentialCard
              serviceRecordStatus={serviceRecordQuery.data?.status as number}
              serviceRecordStatusReason={serviceRecordQuery.data?.status_reason as number}
              credential={accountQuery.data}
              isLoading={serviceRecordQuery.isFetching}
              serviceRecordIdbs={serviceRecordQuery.data?.idsb}
              serviceRecordAccountId={serviceRecordQuery.data?.account_id}
              sendRequest={(formData) =>
                sendRequest({
                  url: `${localconfig.apiBaseUrl}/op/v1/account/${accountQuery.data?.id}`,
                  dataForEndpoint: formData,
                })
              }
              // @ts-ignore
              queryKey={useAccountV2Query.getKey}
            />
          </>
        }
      >
        <>
          {(isSrInPorgressManufacturerNotRegistered || serviceRecordQuery.data?.idsb) && (
            <Warning bgColor={'purple.100'} data-test-id="no-open-task-sr">
              <Stack textAlign={'center'} alignItems={'center'}>
                <HStack>
                  <Box display={'flex'}>
                    <Icon color={'orange'} as={IoIosWarning} boxSize="16" />
                  </Box>
                  <VStack>
                    <Box>{isSrInPorgressManufacturerNotRegistered && <Trans i18nKey={`tasks:open_task.no_open_task`} />}</Box>
                    <Box>{serviceRecordQuery.data?.idsb && <Trans i18nKey={`tasks:idsb`} />}</Box>
                  </VStack>
                </HStack>
              </Stack>
            </Warning>
          )}
          <WorkshopWarning workshop={workshop} isLoading={serviceRecordQuery.isLoading} />

          <PageMetatags title={t('pages:service_record.details.meta.title')} />

          <StatusCard
            permission="service-entries:update"
            serviceRecordStatus={serviceRecordQuery.data?.status as number}
            completedAt={dateTimeTransformer(serviceRecordQuery.data?.completed_at as string)}
            showSubContent={
              (serviceRecordQuery.data?.conversation?.messages && serviceRecordQuery.data?.conversation?.messages?.length > 0) ||
              serviceRecordQuery.data?.status === getStatus(serviceRecordCodes, 'clarification')
            }
            canEdit={Boolean(serviceRecordQuery.data?.open_task?.user_id === user?.id)}
            completedTime={serviceRecordQuery.data?.completed_at}
            currentStatus={serviceRecordQuery.data?.status}
            currentStatusReason={serviceRecordQuery.data?.status_reason}
            fileId={serviceRecordQuery.data?.file_id}
            id={serviceRecordId}
            isLoading={serviceRecordQuery.isFetching}
            isServiceRecord={true}
            hasOpenTask={hasOpenTask}
            reasons={reasons}
            resourceKey={resourceKey}
            shouldHaveFile={true}
            statusCodes={statusCodes?.service_record}
            statusTransitions={statusTransitions?.service_record}
            updateStatus={(data) => {
              if (mazdaHasWlanConnected === true) {
                updateServiceRecordRequestStatusMutation.mutate({ ...data, mazda_wlan_consent: true });
                return;
              }
              updateServiceRecordRequestStatusMutation.mutate(data);
            }}
            subContentOpenByDefault={serviceRecordQuery.data?.status === getStatus(serviceRecordCodes, 'clarification')}
            subContent={
              <ConversationCard
                assignee={serviceRecordQuery.data?.open_task?.assignee}
                currentUserId={user?.id}
                updateServiceRecordRequestStatusMutation={updateServiceRecordRequestStatusMutation.mutate}
                serviceRecordStatus={serviceRecordQuery.data?.status as number}
                updateStatus={(data) => {
                  if (mazdaHasWlanConnected === true) {
                    updateServiceRecordRequestStatusMutation.mutate({ ...data, mazda_wlan_consent: true });
                    return;
                  }
                  updateServiceRecordRequestStatusMutation.mutate(data);
                }}
                // @ts-ignore
                record={serviceRecordQuery.data}
                resourceKey={'service_record'}
                // @ts-ignore
                reasons={allStatusesAndAllStatusReasons}
                statusCodes={serviceRecordCodes}
                // @ts-ignore
                setFile={(document: Document) => {
                  // @ts-ignore
                  setUrl(`document/${document.id}/download`);
                  // @ts-ignore
                  setDownloadDocument(document?.file);
                }}
                sendMessage={(data) =>
                  createConversationMessageMutation.mutateAsync(data).then(() => {
                    queryClient.invalidateQueries({ queryKey: useServiceRecordQuery.getKey(serviceRecordId) });
                    return queryClient.invalidateQueries({ queryKey: ['Tasks'] });
                  })
                }
                canSendMessage={serviceRecordQuery.data?.status === getStatus(serviceRecordCodes, 'clarification')}
              />
            }
          />

          {(srRequestedDeletion || srOnHoldRequestedDeletion) && (
            // @ts-expect-error
            <Box align={'right'}>
              <Button
                mr={1}
                bgColor={'orange.500'}
                variant="primary"
                color="on-accent-muted"
                _hover={{
                  bgColor: 'white',
                  borderColor: 'orange.500',
                  border: '1px',
                  color: 'orange.500',
                }}
                onClick={() => {
                  NiceModal.show(AlertModal as any, {
                    onSubmit: () => {
                      updateServiceRecordRequestStatusMutation
                        .mutateAsync({
                          status: 700,
                          status_reason: null,
                        })
                        .then(() => {
                          return queryClient.invalidateQueries({ queryKey: useServiceRecordQuery.getKey(serviceRecordId) });
                        });
                    },
                    children: t('service_record:abort_deletion.text'),
                    content: {
                      header: t('service_record:abort_deletion.label'),
                      footer: {
                        buttons: {
                          cancelCaption: t('common:cancel'),
                          actionCaption: t('service_record:abort_deletion.label'),
                        },
                      },
                    },
                    catOrange: true,
                  });
                }}
              >
                {t('service_record:abort_deletion.label')}
              </Button>
            </Box>
          )}

          {isSystemPartnerFull && <SystemPartnerCard name={serviceRecordQuery.data?.systempartner?.name} />}
          {hasOpenTask &&
          (serviceRecordQuery.data?.status === getStatus(serviceRecordCodes, 'clarification') ||
            serviceRecordQuery.data?.status === getStatus(serviceRecordCodes, 'on_hold')) ? (
            <RecentlyCheckedCard
              dateTaskCheckedByUser={dateTaskCheckedByUser}
              checkTask={() => {
                // @ts-ignore
                checkTaskMutation.mutate({
                  openTaskId: serviceRecordQuery.data?.open_task?.id,
                });
              }}
            />
          ) : null}
          {Boolean(serviceRecordQuery.data?.legacy) && (
            <Warning>
              <HStack>
                <Icon boxSize="12" color={'orange'} as={IoIosWarning} />
                <Stack>
                  <Text>{t('service_record:pages.detail.legacy.header')}</Text>
                  <Text>{t('service_record:pages.detail.legacy.description')}</Text>
                </Stack>
              </HStack>
            </Warning>
          )}

          {detailsCard}
          <ContentCard header={t('service_record:file.title')}>
            <Stack w={'full'} pt={2}>
              {!serviceRecordQuery.data?.file_id || canEdit ? (
                <FileForm
                  deleteServiceDocumentMutation={deleteServiceDocumentMutation}
                  uploadFileMutation={uploadFileMutation}
                  serviceDocumentExists={!!serviceDocumentId}
                  setCanEdit={setCanEdit}
                  isDisabled={!(isCompleted || openTaskAssignedToCurrentUser)}
                  editCancelButton={
                    serviceRecordQuery.data?.file_id && (
                      <Button onClick={() => setCanEdit(false)} variant={'outline'}>
                        {t('common:cancel')}
                      </Button>
                    )
                  }
                />
              ) : (
                <FileDisplay
                  apiUrl={apiUrl}
                  handleReplace={() => {
                    setCanEdit(true);
                  }}
                  canEdit={isCompleted || (isMember('service-entries:update') && openTaskAssignedToCurrentUser)}
                  record={serviceRecordQuery.data}
                  resourceKey={'service-record'}
                />
              )}
            </Stack>
          </ContentCard>
          <DocumentsCard
            isLoadingParent={serviceRecordQuery.isFetching}
            // @ts-ignore
            documents={serviceRecordQuery.data?.service_activity?.service.documents}
            download={(document) => {
              // @ts-ignore
              setUrl(`document/${document.id}/download`);
              setDownloadDocument(document.file);
              setClick(true);
            }}
            remove={(documentId: number) =>
              deleteServiceRecordDocumentMutations.mutateAsync(documentId).then(() => {
                return queryClient.invalidateQueries({ queryKey: useServiceRecordQuery.getKey(serviceRecordId) });
              })
            }
            uploadMutation={(formData) => {
              formData.append('subject_type', 'service');
              //@ts-ignore
              formData.append('subject_id', serviceRecordQuery.data?.service_activity?.service?.id);
              return uploadServiceRecordDocumentMutation.mutateAsync(formData).then(() => {
                return queryClient.invalidateQueries({ queryKey: useServiceRecordQuery.getKey(serviceRecordId) });
              });
            }}
          />
        </>
        <TasksTableCard tasks={tasks} isLoading={serviceRecordQuery.isPending} />
        <ActivityLogCard
          workshopName={workshop?.name}
          resourceKey={resourceKey}
          queryKey={useServiceRecordQuery.getKey(serviceRecordId)}
          activities={serviceRecordQuery.data?.activities}
          account_id={serviceRecordId}
        />
      </ContentLayout>
    </>
  );
};

export default ServiceRecordDetailsPage;
